import { useGlobal } from '@/context/GlobalContext';
import { usePageContent } from '@/hooks/usePageContent';
import { Button } from '@/components/ui/button';
import { Calendar, Award, Building2, Globe, Users, Target, Lightbulb, Cpu, Handshake } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO, { organizationSchema } from '@/components/SEO';

const AboutPage = () => {
  const { settings } = useGlobal();
  const { content, loading: contentLoading } = usePageContent('about');

  // Get content with defaults
  const hero = content.hero || {};
  const whoWeAre = content.who_we_are || {};
  const whatWeDoCMS = content.what_we_do || {};
  const howWeWork = content.how_we_work || {};
  const journey = content.journey || {};
  const standards = content.standards || {};
  const cta = content.cta || {};

  // Default values
  const defaults = {
    hero: {
      badge: "ABOUT US",
      headline: "Engineering Excellence Since 1995",
      description: "Trinity Electronics is your engineering and solutions partner for imaging and safety systems — bridging the gap between your vision and production.",
      background_image: "https://images.unsplash.com/photo-1598935898639-81586f7d2129?w=1200&q=80"
    },
    who_we_are: {
      title: "Who We Are",
      paragraphs: [
        "Founded in 1995 in Taipei, Taiwan, Trinity Electronics started with a clear mission: to deliver imaging solutions built on quality, reliability, and deep technical expertise.",
        "Today, we operate as an engineering and solutions partner. We design and develop systems in-house and work with a network of trusted manufacturing partners who share our commitment to excellence.",
        "Our strength isn't in owning factories — it's in understanding the technology deeply and matching the right solution to your specific needs."
      ],
      stats: [
        { icon: "Building2", value: "Taipei", label: "Headquarters" },
        { icon: "Globe", value: "25+", label: "Countries Served" },
        { icon: "Cpu", value: "30+", label: "Years in Imaging" },
        { icon: "Users", value: "7", label: "Languages Supported" }
      ]
    },
    what_we_do: {
      title: "What We Do",
      description: "Three divisions, each with a distinct approach tailored to the market.",
      divisions: [
        { title: 'Mobility Imaging', description: 'OEM/ODM development and private label products — dashcams, e-mirrors, MDVRs, vehicle cameras.', role: 'Design & Develop' },
        { title: 'Surveillance Systems', description: 'Sourcing partner with access to vetted factories, NDAA-compliant options, and custom configurations.', role: 'Source & Support' },
        { title: 'Ultrasonic Deterrence', description: 'Consulting and exclusive distribution for humane animal deterrence systems worldwide.', role: 'Consult & Distribute' }
      ]
    },
    how_we_work: {
      title: "How We Work",
      values: [
        { icon: "Target", title: 'Engineering Excellence', description: 'Deep technical expertise in imaging — from optics to ISP tuning.' },
        { icon: "Handshake", title: 'Partnership Approach', description: 'We succeed when our clients succeed. Long-term relationships over quick sales.' },
        { icon: "Lightbulb", title: 'Solution-Focused', description: 'We start with your problem, not our product catalog.' }
      ]
    },
    journey: {
      title: "Our Journey",
      milestones: [
        { year: '1995', title: 'Founded in Taipei', description: 'Trinity Electronics established as an imaging solutions company.' },
        { year: '2000', title: 'Automotive Division', description: 'Expanded into vehicle imaging systems and dashcams.' },
        { year: '2008', title: 'Partner Network Established', description: 'Built long-term relationships with certified manufacturing partners across Asia.' },
        { year: '2015', title: 'Ultrasonic Division', description: 'Became exclusive distributor for ultrasonic deterrence (outside Greater China).' },
        { year: '2020', title: 'AI & ADAS Integration', description: 'Introduced AI-powered MDVR and driver monitoring solutions.' },
        { year: '2024', title: 'Global Reach', description: 'Serving clients across Asia, Europe, and the Americas with multilingual support.' }
      ]
    },
    standards: {
      title: "Standards & Compliance",
      description: "We work with certified partners and support clients through the certification process.",
      certifications: ['CE Marking', 'FCC Compliance', 'RoHS', 'ISO 9001', 'ISO 14001', 'NDAA Compliant', 'E-Mark (Automotive)', 'IP67 Testing']
    },
    cta: {
      title: "Let's Build Together",
      description: "Whether you need a complete imaging system, a sourcing partner, or expert consulting — our team is ready to help."
    }
  };

  // Merge CMS content with defaults
  const h = { ...defaults.hero, ...hero };
  const wwa = { ...defaults.who_we_are, ...whoWeAre };
  const wwd = { ...defaults.what_we_do, ...whatWeDoCMS };
  const hww = { ...defaults.how_we_work, ...howWeWork };
  const j = { ...defaults.journey, ...journey };
  const s = { ...defaults.standards, ...standards };
  const c = { ...defaults.cta, ...cta };

  const milestones = j.milestones || defaults.journey.milestones;
  const values = hww.values || defaults.how_we_work.values;
  const whatWeDo = wwd.divisions || defaults.what_we_do.divisions;

  // Icon mapping for dynamic rendering
  const iconMap = { Target, Handshake, Lightbulb, Building2, Globe, Cpu, Users };

  return (
    <div className="min-h-screen bg-[#1a1f2e]" data-testid="about-page">
      <SEO 
        title="About Us - Engineering Excellence Since 1995"
        description="Trinity Electronics is your engineering partner for imaging and safety systems. Founded in 1995 in Taiwan, we serve 25+ countries with automotive cameras, surveillance, and ultrasonic solutions."
        keywords="Trinity Electronics, Anchilex, Taiwan camera manufacturer, imaging solutions company, OEM ODM partner Taiwan, vehicle camera engineering, wholesale camera supplier, camera distributor Taiwan"
        canonicalUrl="https://www.trinity-tw.com/about"
        structuredData={organizationSchema}
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url(${h.background_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1f2e]/30 via-[#1a1f2e]/50 to-[#1a1f2e]/60" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#1e2530] border border-[#3d4758] text-sm text-slate-300 mb-6">
            {h.badge}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 font-['Montserrat']">
            {h.headline}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {h.description}
          </p>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6 font-['Montserrat']">
                {wwa.title}
              </h2>
              <div className="space-y-4 text-slate-400">
                {(wwa.paragraphs || defaults.who_we_are.paragraphs).map((para, idx) => (
                  <p key={idx} dangerouslySetInnerHTML={{ __html: para.replace(/engineering and solutions partner/gi, '<span class="text-white font-medium">engineering and solutions partner</span>') }} />
                ))}
              </div>
              <div className="mt-8">
                <a href={settings.teams_scheduling_url} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full">
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule a Conversation
                  </Button>
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-6">
                <Building2 className="w-8 h-8 text-[#a78bfa] mb-4" />
                <div className="text-2xl font-bold text-white font-['Montserrat']">Taipei</div>
                <div className="text-slate-400 text-sm">Headquarters</div>
              </div>
              <div className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-6">
                <Globe className="w-8 h-8 text-[#a78bfa] mb-4" />
                <div className="text-2xl font-bold text-white font-['Montserrat']">25+</div>
                <div className="text-slate-400 text-sm">Countries Served</div>
              </div>
              <div className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-6">
                <Cpu className="w-8 h-8 text-[#a78bfa] mb-4" />
                <div className="text-2xl font-bold text-white font-['Montserrat']">30+</div>
                <div className="text-slate-400 text-sm">Years in Imaging</div>
              </div>
              <div className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-6">
                <Users className="w-8 h-8 text-[#a78bfa] mb-4" />
                <div className="text-2xl font-bold text-white font-['Montserrat']">7</div>
                <div className="text-slate-400 text-sm">Languages Supported</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {wwd.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {wwd.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whatWeDo.map((division, idx) => (
              <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#5b3589]/20 text-xs text-[#a78bfa] font-medium mb-4">
                  {division.role}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 font-['Montserrat']">
                  {division.title}
                </h3>
                <p className="text-slate-400 text-sm">{division.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-12 text-center font-['Montserrat']">
            {hww.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, idx) => {
              const IconComponent = iconMap[value.icon] || Target;
              return (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 bg-[#5b3589]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-8 h-8 text-[#a78bfa]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2 font-['Montserrat']">
                    {value.title}
                  </h3>
                  <p className="text-slate-400">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-12 text-center font-['Montserrat']">
            {j.title}
          </h2>
          <div className="max-w-3xl mx-auto">
            {milestones.map((milestone, idx) => (
              <div key={idx} className="flex gap-6 mb-8 last:mb-0">
                <div className="w-20 shrink-0">
                  <span className="font-mono text-[#a78bfa] font-bold text-lg">
                    {milestone.year}
                  </span>
                </div>
                <div className="relative pl-6 border-l border-slate-700">
                  <div className="absolute -left-1.5 top-1.5 w-3 h-3 bg-[#5b3589] rounded-full" />
                  <h3 className="font-semibold text-white font-['Montserrat'] mb-1">
                    {milestone.title}
                  </h3>
                  <p className="text-slate-400 text-sm">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Standards */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {s.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {s.description}
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(s.certifications || defaults.standards.certifications).map((cert, idx) => (
              <div key={idx} className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-4 text-center">
                <span className="text-slate-300 text-sm">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6 font-['Montserrat']">
            {c.title}
          </h2>
          <p className="text-slate-400 mb-8">
            {c.description}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full px-8">
                Contact Us
              </Button>
            </Link>
            <Link to="/engineering">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-full px-8">
                Engineering Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
